/**
 * Le suivi d'audience, `RM-ANX-003`, `spec-technique.md` § Le suivi d'audience.
 *
 * TROIS REGLES COMMANDENT CE FICHIER, ET AUCUNE N'EST UN DETAIL D'IMPLEMENTATION.
 *
 *   1. AUCUN COOKIE, AUCUN STOCKAGE LOCAL. `persistence: 'memory'` et
 *      `person_profiles: 'identified_only'`. C'est ce qui dispense la page d'un bandeau de
 *      consentement, et `AC-ANX-012` le mesure sur la page servie.
 *   2. TROIS EVENEMENTS, ET PAS UN DE PLUS : l'affichage de la page, le clic sur un appel a
 *      l'action avec sa position, et l'envoi reussi d'un formulaire. `autocapture` est donc COUPE :
 *      laisse a sa valeur par defaut, il enverrait un evenement par clic sur n'importe quel
 *      element. `AC-ANX-013` compte les noms emis.
 *   3. LE SCRIPT NE BLOQUE JAMAIS L'AFFICHAGE. Il est pose par `next/script` en `lazyOnload`, donc
 *      apres l'evenement `load` de la fenetre, et il va chercher le fichier de PostHog lui-meme.
 *      `AC-ANX-014` le mesure.
 *
 * POURQUOI CE FICHIER ECRIT DU JAVASCRIPT DANS UNE CHAINE, ET N'IMPORTE PAS `posthog-js`. Deux
 * raisons, et la seconde est la plus contraignante :
 *
 *   - la bibliotheque installee par npm entrerait dans le paquet envoye au navigateur ; le fichier
 *     alle chercher a la demande n'y entre pas ;
 *   - la page d'accueil N'A AUCUN COMPOSANT CLIENT, `AC-CNT-020` et la derogation de
 *     `spec-design.md` v7 l'exigent. Un composant qui appellerait `posthog.init` dans un effet en
 *     serait un. Une balise `<script>` posee par un composant SERVEUR n'en est pas un.
 */

/** Les trois seuls noms d'evenement que ce site emet. */
export const EVENEMENTS = Object.freeze({
  page: 'page_vue',
  reservation: 'clic_reservation',
  formulaire: 'formulaire_envoye',
});

/** L'attribut que `components/ui/Bouton.tsx` pose deja, et qui porte la position dans la page. */
export const ATTRIBUT_DE_POSITION = 'data-bouton';

/** La cle et l'hote, publics par nature, et c'est pourquoi ils portent le prefixe du framework. */
export function configurationDuSuivi(): { readonly cle: string; readonly hote: string } | null {
  const cle = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  const hote = process.env.NEXT_PUBLIC_POSTHOG_HOST;
  // PAS DE VALEUR DE REPLI, INV-SEC-001 : sans les deux, le suivi n'existe pas, et il ne bascule
  // pas sur un projet devine. C'est aussi ce qui laisse la suite de tests tourner sans compte.
  if (!cle || !hote) return null;
  return { cle, hote };
}

/**
 * Le code pose dans la page. Il est rendu par un composant SERVEUR, en `lazyOnload`.
 *
 * ⚠️ IL VA CHERCHER `array.js` LUI-MEME plutot que d'etre declare en `src` sur la balise : c'est le
 * seul moyen d'appeler `init` APRES le chargement sans ecrire de composant client, `next/script`
 * n'acceptant `onLoad` que dans un fichier `'use client'`.
 */
export function scriptDuSuivi({ cle, hote }: { readonly cle: string; readonly hote: string }): string {
  const options = JSON.stringify({
    api_host: hote,
    // LES DEUX REGLAGES QUE LA SPEC NOMME, et qui font qu'aucun cookie n'est ecrit.
    persistence: 'memory',
    person_profiles: 'identified_only',
    // TROIS EVENEMENTS SEULEMENT : ces quatre reglages coupent tout ce que PostHog emet de
    // lui-meme, y compris la page vue, que ce fichier envoie sous son propre nom.
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    disable_session_recording: true,
    // NI SONDAGE NI DRAPEAU : deux appels reseau de plus, pour des fonctions que ce site n'utilise
    // pas. Les couper tient la cible de performance.
    disable_surveys: true,
    advanced_disable_feature_flags: true,
  });

  return `(function(){
  var d=document,s=d.createElement('script');
  s.src=${JSON.stringify(`${hote}/static/array.js`)};s.async=true;
  s.onload=function(){
    if(!window.posthog||!window.posthog.init){return;}
    window.posthog.init(${JSON.stringify(cle)},${options});
    window.posthog.capture(${JSON.stringify(EVENEMENTS.page)});
    d.addEventListener('click',function(e){
      var c=e.target&&e.target.closest?e.target.closest('a[href="/inscription"]'):null;
      if(!c){return;}
      window.posthog.capture(${JSON.stringify(EVENEMENTS.reservation)},{position:c.getAttribute(${JSON.stringify(ATTRIBUT_DE_POSITION)})||'inconnue'});
    },true);
  };
  d.head.appendChild(s);
})();`;
}

/**
 * L'envoi reussi d'un formulaire, appele depuis les deux parcours.
 *
 * ILS SONT DEJA DES COMPOSANTS CLIENT, sous la derogation de `spec-design.md` v7 : cet appel n'en
 * cree aucun. Il ne fait rien quand le suivi n'est pas installe, ce qui est le cas en test.
 */
export function envoiReussi(formulaire: 'inscription' | 'contact'): void {
  if (typeof window === 'undefined') return;
  const suivi = (window as { posthog?: { capture?: (nom: string, p?: unknown) => void } }).posthog;
  suivi?.capture?.(EVENEMENTS.formulaire, { formulaire });
}
